/**
 * Session Manager
 *
 * Handles session timeout rules:
 * - Inactivity timeout: 8 hours (no API calls)
 *
 * Does not use timers or popups - only checks on:
 * - API calls
 * - Token refresh attempts
 * - Route navigation
 */
import { logger } from "@/infrastructure/services/LoggerService";
// Session timeout constants (in milliseconds)
const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
// SessionStorage keys
const LAST_ACTIVITY_KEY = "session-last-activity";
/**
 * Initialize a new session
 * Called after successful login
 */
export function initializeSession() {
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    logger.debug("Session initialized", {
        start: new Date(now).toISOString(),
        inactivityTimeout: INACTIVITY_TIMEOUT / 1000 / 60 / 60 + "h",
    });
}
/**
 * Update last activity timestamp
 * Called on every authenticated API call
 */
export function updateActivity() {
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    logger.debug("Activity updated:", new Date(now).toISOString());
}
/**
 * Check if the current session is valid
 * Returns detailed session state
 */
export function checkSessionValidity() {
    const lastActivityStr = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    logger.debug("check Session Validity");
    // No session data found
    if (!lastActivityStr) {
        logger.debug("No session data found");
        return {
            isValid: false,
            reason: "no_session",
        };
    }
    const now = Date.now();
    const lastActivity = parseInt(lastActivityStr, 10);
    // Validate parsed timestamp
    if (isNaN(lastActivity)) {
        logger.debug("Session data is corrupted (invalid timestamp)");
        return {
            isValid: false,
            reason: "no_session",
        };
    }
    // Check inactivity timeout
    const timeSinceActivity = now - lastActivity;
    if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        logger.debug("Session expired due to inactivity", {
            lastActivity: new Date(lastActivity).toISOString(),
            timeSinceActivity: (timeSinceActivity / 1000 / 60).toFixed(2) + "m",
            threshold: (INACTIVITY_TIMEOUT / 1000 / 60).toFixed(2) + "m",
            timeSinceActivityMs: timeSinceActivity,
            thresholdMs: INACTIVITY_TIMEOUT,
        });
        return {
            isValid: false,
            reason: "inactivity",
            lastActivity,
        };
    }
    // Session is valid
    const inactivityRemaining = INACTIVITY_TIMEOUT - timeSinceActivity;
    logger.debug("Session is valid", {
        lastActivity: new Date(lastActivity).toISOString(),
        inactivityRemaining: (inactivityRemaining / 1000 / 60).toFixed(2) + "m",
        inactivityRemainingMs: inactivityRemaining,
    });
    return {
        isValid: true,
        lastActivity,
        inactivityRemaining,
    };
}
/**
 * Clear session data
 * Called on logout or session expiry
 */
export function clearSession() {
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    logger.debug("Session data cleared");
}
/**
 * Get session timeout configuration
 * Useful for displaying information or testing
 */
export function getSessionConfig() {
    return {
        inactivityTimeout: INACTIVITY_TIMEOUT,
        inactivityTimeoutHours: INACTIVITY_TIMEOUT / 1000 / 60 / 60,
    };
}
